import {
  Card,
  CardContent,
  Grid,
  Typography,
  CardHeader,
  Divider,
  Chip,
  Box,
  Alert,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSingleAddressQuery } from '../../rest/useAddressChange';
import { getStatusLabel } from '../../Utils/Label/status-label';
import PageLoader from '../../Components/Spinner/PageLoader';
import ModalsWrapper from '../../Components/Modals/ModalsWrapper';
import PdfAndImageView from '../../Components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from '../../Components/pdfAndImageView/CommonTypoGraphy';
import OfflinePaymentCardInformation from '../new-member-applications/IndexFile/paymentInformation/offline/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from '../../Components/CommonDetailItem/CommonDetailItem';

const SingleAddressApplication = () => {
  const { id } = useParams();
  const { data, fetchData, loading } = useSingleAddressQuery(
    id !== undefined ? id : ''
  );
  const [open, setOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  return (
    <>
      {!loading ? (
        <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 }, mb: 3 }}>
          <Card
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 700,
                    color: '#1e293b',
                  }}
                >
                  Address Change Application Details
                </Typography>
              }

              sx={{
                borderBottom: '1px solid #edf2f7',
                py: 1.25,
                px: { xs: 2, sm: 2.5 },
                backgroundColor: '#fafbfc',
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } },
              }}
            >
              {data ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Rejection / Note banner if any */}
                  {data?.address_change_application_reject_reason && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: 1.5,
                        py: 0.5,
                        px: 1.5,
                        fontSize: '0.875rem',
                        '& .MuiAlert-message': { py: 0.25 },
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        component='span'
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Note / Rejection Reason:
                      </Typography>{' '}
                      {data.address_change_application_reject_reason}
                    </Alert>
                  )}

                  {/* Section 1: General & Contact Information */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        General & Contact Information
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Application Date'
                        value={formatDate(data?.address_change_application_created_at)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Company Name'
                        value={formatValue(data?.user_member_company_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Status'
                        value={
                          data?.address_change_application_status
                            ? getStatusLabel(data.address_change_application_status)
                            : 'N/A'
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Email'
                        value={formatValue(data?.address_change_application_email)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Phone No'
                        value={formatValue(data?.address_change_application_phone)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Cellphone No'
                        value={formatValue(data?.address_change_application_cellphone)}
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 2: New Address Details */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        New Address Details
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Address'
                        value={formatValue(data?.address_change_application_addressline)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Division'
                        value={formatValue(data?.division_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='District'
                        value={formatValue(data?.district_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Thana'
                        value={formatValue(data?.thana_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Area Name'
                        value={formatValue(data?.area_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Postal Code'
                        value={formatValue(data?.address_change_application_postal_code)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Police Station'
                        value={formatValue(data?.address_change_application_police_station)}
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 3: Uploaded Documents */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Uploaded Documents
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Civil Aviation'
                        value={
                          data?.address_change_application_civil_aviation ? (
                            <CommonTypoGraphy
                              name='View Civil Aviation'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  data.address_change_application_civil_aviation
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Trade License'
                        value={
                          data?.address_change_application_trade_license ? (
                            <CommonTypoGraphy
                              name='View Trade License'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  data.address_change_application_trade_license
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Forwarding Letter'
                        value={
                          data?.address_change_application_form ? (
                            <CommonTypoGraphy
                              name='View Forwarding Letter'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(data.address_change_application_form);
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                    </Grid>
                  </Box>

                  {/* Section 4: Payment Info */}
                  {data?.paymentInfo && (
                    <>
                      <Divider sx={{ borderColor: '#f1f5f9' }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box
                            sx={{
                              width: 3.5,
                              height: 15,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                            }}
                          />
                          <Typography
                            variant='subtitle2'
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.925rem',
                              color: '#0f172a',
                              letterSpacing: 0.1,
                            }}
                          >
                            Payment Information
                          </Typography>
                          <Chip
                            label={
                              data?.paymentInfo?.type === 'online'
                                ? 'Online Payment'
                                : 'Offline Payment'
                            }
                            size='small'
                            color={data?.paymentInfo?.type === 'online' ? 'primary' : 'default'}
                            variant='outlined'
                            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ml: 0.5 }}
                          />
                        </Box>
                        <OfflinePaymentCardInformation data={data?.paymentInfo} />
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.secondary'
                  textAlign='center'
                  py={4}
                >
                  No Data found
                </Typography>
              )}
            </CardContent>
          </Card>

          <ModalsWrapper
            modalData={{ title: 'View Document' }}
            setShowModal={setOpen}
            showModal={open}
          >
            <PdfAndImageView file={imageFile} />
          </ModalsWrapper>
        </Box>
      ) : (
        <PageLoader />
      )}
    </>
  );
};

export default SingleAddressApplication;


